/** Pruebas de impacto: qué figura está bajo el puntero, qué figuras toca una selección. */
import type { Shape } from '../../shared/types.ts'
import { distToSegment, pointInBox, pointInPolygon, polygonBounds, rotatePoint, type Box } from '../lib/geom.ts'
import { distToRelation } from '../mindmap/edges.ts'
import { shapeBounds } from '../state/shapes.ts'
import { inkPoints } from './render-shape.ts'

/** Convierte un punto de mundo al espacio local (sin rotar) de la figura. */
function toLocal(shape: Shape, x: number, y: number): { x: number; y: number } {
  if (!shape.rotation) return { x: x - shape.x, y: y - shape.y }
  const cx = shape.x + shape.w / 2
  const cy = shape.y + shape.h / 2
  const p = rotatePoint(x, y, cx, cy, -shape.rotation)
  return { x: p.x - shape.x, y: p.y - shape.y }
}

export function hitShape(shape: Shape, x: number, y: number, tolerance: number): boolean {
  const local = toLocal(shape, x, y)

  if (shape.type === 'draw') {
    const half = shape.size / 2 + tolerance
    const pts = inkPoints(shape)
    if (pts.length === 1) return Math.hypot(local.x - pts[0].x, local.y - pts[0].y) <= half
    for (let i = 1; i < pts.length; i++) {
      const a = pts[i - 1]
      const b = pts[i]
      if (distToSegment(local.x, local.y, a.x, a.y, b.x, b.y) <= half) return true
    }
    return false
  }

  const box: Box = { x: 0, y: 0, w: shape.w, h: shape.h }

  if (shape.type === 'shape') {
    const isLine = shape.kind === 'line' || shape.kind === 'arrow'
    if (isLine) {
      return distToSegment(local.x, local.y, 0, 0, shape.w, shape.h) <= shape.strokeWidth / 2 + tolerance + 4
    }
    if (shape.fill && shape.fill !== 'none') return pointInBox(local.x, local.y, box, tolerance)
    // Sin relleno: solo el contorno es "tocable" (como en Whiteboard).
    const inner = shape.strokeWidth + tolerance + 6
    const insideOuter = pointInBox(local.x, local.y, box, tolerance + shape.strokeWidth)
    const insideInner =
      local.x > inner && local.y > inner && local.x < shape.w - inner && local.y < shape.h - inner
    if (shape.text) return insideOuter
    return insideOuter && !insideInner
  }

  return pointInBox(local.x, local.y, box, tolerance)
}

/** Figura más alta (mayor z) bajo el punto. */
export function hitTest(shapes: Shape[], x: number, y: number, zoom: number): Shape | null {
  const tolerance = 6 / zoom
  for (let i = shapes.length - 1; i >= 0; i--) {
    const shape = shapes[i]
    if (shape.locked) continue
    if ((shape.type === 'topic' || shape.type === 'relation') && shape.hidden) continue
    if (shape.type === 'relation') {
      // La relación se toca por su curva, no por su caja (que es enorme).
      const a = shapes.find((s) => s.id === shape.from)
      const b = shapes.find((s) => s.id === shape.to)
      if (a?.type === 'topic' && b?.type === 'topic' && distToRelation(x, y, a, b) < tolerance + 5) {
        return shape
      }
      continue
    }
    if (hitShape(shape, x, y, tolerance)) return shape
  }
  return null
}

/** Figuras que intersecan un rectángulo (selección por marco). */
export function shapesInBox(shapes: Shape[], box: Box): Shape[] {
  return shapes.filter((shape) => {
    if (shape.locked) return false
    if ((shape.type === 'topic' || shape.type === 'relation') && shape.hidden) return false
    if (shape.type === 'relation') return false
    const b = shapeBounds(shape)
    return !(b.x + b.w < box.x || box.x + box.w < b.x || b.y + b.h < box.y || box.y + box.h < b.y)
  })
}

/** Figuras cuyo centro cae dentro del polígono del lazo. */
export function shapesInLasso(shapes: Shape[], poly: number[]): Shape[] {
  const bounds = polygonBounds(poly)
  return shapes.filter((shape) => {
    if (shape.locked) return false
    if ((shape.type === 'topic' || shape.type === 'relation') && shape.hidden) return false
    if (shape.type === 'relation') return false
    const b = shapeBounds(shape)
    const cx = b.x + b.w / 2
    const cy = b.y + b.h / 2
    if (!pointInBox(cx, cy, bounds)) return false
    return pointInPolygon(cx, cy, poly)
  })
}
